import { useMemo } from 'react'

import { enumCodec, QuerySchema, QuerySetter, useQuerySettings } from '@/hooks/useQueryState'

import type { ReaderLayout } from '../shared/reader-layout'

/** 画像ビューア（コミックリーダー）の表示設定 */
export type ImageReaderSettings = ReaderLayout

export const DEFAULT_IMAGE_READER_SETTINGS: ImageReaderSettings = {
  direction: 'ttb',
  dragMode: 'free',
  pageMode: 'single',
  dualReadingOrder: 'rtl',
}

const buildSchema = (defaults: ImageReaderSettings): QuerySchema<ImageReaderSettings> => ({
  direction: { param: 'direction', codec: enumCodec(['ttb', 'rtl', 'ltr'], defaults.direction) },
  dragMode: { param: 'drag', codec: enumCodec(['free', 'snap'], defaults.dragMode) },
  pageMode: { param: 'page-mode', codec: enumCodec(['single', 'dual', 'dual-cover'], defaults.pageMode) },
  dualReadingOrder: {
    // 'order' は商品一覧のソート順 (asc/desc) で使われているため衝突を避ける
    param: 'read-order',
    codec: enumCodec(['ltr', 'rtl'], defaults.dualReadingOrder),
  },
})

/**
 * 画像ビューアの表示設定を URL クエリパラメータで管理するフック。
 *
 * リロードや URL 共有で表示設定（方向 / 送り方 / ページ配置 / 読み方向）を復元できる。
 * URL に無い項目は defaults（作品ごとに覚えた設定など）を使う。defaults は再レンダーで作り直さないこと。
 */
export const useImageReaderSettingsQuery = (
  defaults: ImageReaderSettings = DEFAULT_IMAGE_READER_SETTINGS,
): [ImageReaderSettings, QuerySetter<ImageReaderSettings>] => {
  const schema = useMemo(() => buildSchema(defaults), [defaults])
  return useQuerySettings(schema)
}
